import React, { JSX } from "react";
import { Box, Typography, Tooltip, useTheme } from "@mui/material";
import { Query, queryOps } from "system/query/Query";
import SubqueriesSentimentTooltip from "../Tooltips/SubqueriesSentimentTooltip";
import { useTranslation } from "react-i18next";
import ISOPercentage from "components/formatting/ISOPercentage";
import { QueryLeafNode } from "system/query/QueryLeafNode";

export interface SubqueriesSentimentProps {
  rowResponse: any;
  rootResponse: any;
  setValueDrilldownQuery: (query?: Query) => void;
  setValueDrilldownTitle: (title: string) => void;
}

export default function SubqueriesSentiment(props: SubqueriesSentimentProps): JSX.Element {
  const { rowResponse, rootResponse, setValueDrilldownQuery, setValueDrilldownTitle } = props;

  const theme = useTheme();
  const { i18n } = useTranslation();

  const [sentimentIsHovered, setSentimentIsHovered] = React.useState<boolean>(false);

  const rowBody = rowResponse.responseBody;
  const rootBody = rootResponse.responseBody;

  const getSentimentValue = (sentimentBody: any, sentimentType: string): number => {
    const activeBucket = (sentimentBody?._buckets || []).find((bucket: any) => bucket._key === sentimentType);
    return activeBucket?._count || 0;
  };

  const rootSentimentPositive = getSentimentValue(rootBody?.sentiment, "p");
  const rootSentimentNegative = getSentimentValue(rootBody?.sentiment, "n");
  const rootSentimentNeutral = getSentimentValue(rootBody?.sentiment, "0");

  const rowSentimentPositive = getSentimentValue(rowBody?.sentiment, "p");
  const rowSentimentNegative = getSentimentValue(rowBody?.sentiment, "n");
  const rowSentimentNeutral = getSentimentValue(rowBody?.sentiment, "0");

  const rootSentimentPercent = rootSentimentPositive / (rootSentimentPositive + rootSentimentNegative) || 0;
  const rowSentimentPercent = rowSentimentPositive / (rowSentimentPositive + rowSentimentNegative) || 0;

  const sentimentPercentChange: number = parseFloat(((rowSentimentPercent - rootSentimentPercent) * 100.0).toFixed(1));

  const sentimentWidth = `${(rowSentimentPercent * 100).toFixed(2)}%`;

  return (
  <Tooltip title={<SubqueriesSentimentTooltip
    rootSentimentPositive={rootSentimentPositive}
    rootSentimentNeutral={rootSentimentNeutral}
    rootSentimentNegative={rootSentimentNegative}
    rowSentimentPositive={rowSentimentPositive}
    rowSentimentNeutral={rowSentimentNeutral}
    rowSentimentNegative={rowSentimentNegative}
  />}
    open={sentimentIsHovered}
    followCursor={true}>
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, height: "100%", maxHeight: "50px", position: "relative", cursor: "pointer" }}
      onMouseEnter={() => {
        setSentimentIsHovered(true);
      }}
      onMouseLeave={() => {
        setSentimentIsHovered(false);
      }}
      onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();
        const drilldownQuery = new Query({ query: { op: queryOps.contains, fields: ["sentiment"], value: "p" } as QueryLeafNode});
        setValueDrilldownTitle(`${i18n.t("sentiment")}: ${i18n.t("positive")}`);
        setValueDrilldownQuery(drilldownQuery);
      }}
      >
      <Box>
        <ISOPercentage hideTooltip={true} value={rowSentimentPercent} sx={{ fontWeight: "bold", color: theme.colors.namedColors.positiveDark }} typographyVariant={"subtitle2"} />
      </Box>
      <Box sx={{ textAlign: "right" }}>
        <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontWeight: "bold" }}>
          <span style={{ display: "inline-block", verticalAlign: "middle"}}>
            {sentimentPercentChange >= 0 && "+"}
            {sentimentPercentChange}
          </span>
        </Typography>
      </Box>
      <Box sx={{ width: "100%", height: "26px", position: "relative", gridColumn: "1 / span 2" }}>
        <Box
          sx={{ height: "100%", backgroundColor: theme.colors.namedColors.negative, position: "relative" }} 
          onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            event.stopPropagation();
            event.preventDefault();
            const drilldownQuery = new Query({ query: { op: queryOps.contains, fields: ["sentiment"], value: "n" } as QueryLeafNode});
            setValueDrilldownTitle(`${i18n.t("sentiment")}: ${i18n.t("negative")}`);
            setValueDrilldownQuery(drilldownQuery);
          }}
        >
          <Box sx={{ width: 0, border: `2px solid ${theme.palette.divider}`, position: "absolute", top: -4, bottom: -4, left: `${rootSentimentPercent * 100}%` }} />
          <Box
            sx={{ width: sentimentWidth ?? 0, height: "100%", backgroundColor: theme.colors.namedColors.positive }} 
            onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
              event.stopPropagation();
              event.preventDefault();
              const drilldownQuery = new Query({ query: { op: queryOps.contains, fields: ["sentiment"], value: "p" } as QueryLeafNode});
              setValueDrilldownTitle(`${i18n.t("sentiment")}: ${i18n.t("positive")}`);
              setValueDrilldownQuery(drilldownQuery);
            }} />
          <Box sx={{ width: 0, border: `2px solid ${theme.palette.divider}`, position: "absolute", top: 0, bottom: 0, left: `${rootSentimentPercent * 100}%` }} />
        </Box>
      </Box>
    </Box>
  </Tooltip>
  );
}